import { Link, LinkComponent } from '@tanstack/react-router';
import ExternalLink from 'components/ExternalLink/ExternalLink.component';
import { IconRoot } from 'components/Icon/style/Icon.style';
import {
  MenuTabLink,
  MenuTabsWrapper,
} from 'components/MenuTabs/style/MenuTabs.style';
import colors from 'style-utils/colors';
import createExtractCssFromProps from 'style-utils/createExtractCssFromProps';
import { withPadding, WithPaddingProps } from 'style-utils/dimensionsMixins';
import media from 'style-utils/media-queries';
import { withOneLineEllipsis } from 'style-utils/overflow';
import { withSecondaryTypeface } from 'style-utils/text';
import { Flex } from 'style/flexbox.style';
import styled, { css, StyledProps } from 'styled-components';

export const SlideInContentWrapper = styled(Flex)`
  width: 100vw;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;

  & > * {
    width: 100%;
  }
`;
SlideInContentWrapper.defaultProps = {
  flexDirection: 'row',
  flexWrap: 'wrap',
  justify: 'flex-start',
  align: 'flex-start',
  alignContent: 'space-between',
};

export const CloseButton = styled.button`
  display: inline-flex;
  position: absolute;
  top: 16px;
  right: 16px;
  justify-content: center;
  align-items: center;
  padding: 0 8px;
  outline: none;
  border: none;
  background: ${colors.transparent};
`;
CloseButton.defaultProps = {
  type: 'button',
  'aria-label': 'close',
  title: 'close',
};

export const MenuHeaderWrapper = styled.div`
  height: 112px;
  width: 100%;
  position: relative;
  ${withSecondaryTypeface};
  background: #222;
  box-shadow: 0 2px 20px 0 rgba(0, 0, 0, 0.08);
  font-size: 24px;
  padding: 27px 32px 0 18px;
  font-weight: 900;
  letter-spacing: 0.48px;
  word-break: break-word;
  overflow: hidden;
  color: ${colors.white};

  ${media.tablet`
    height: 136px;
    font-size: 40px;
    padding: 35px 70px 0;
  `};
`;

export const MenuHeading = styled.h3`
  color: inherit;
  margin: 0;
  padding: 0;
  max-width: 100%;
  display: inline-block;
  text-transform: capitalize;
  ${withOneLineEllipsis};
`;

export const SubHeaderTappableList = styled.ul`
  list-style: none;
  display: flex;
  width: 100%;
  padding: 0 16px;
  overflow: hidden;
  justify-content: flex-start;
  align-items: flex-start;
  position: relative;
  gap: 8px;

  top: -25px;
  z-index: 2;

  ${media.tablet`
    padding: 0 70px;
  `};
`;

type SubHeaderTappableListItemProps = StyledProps<{
  mobileFlex?: string;
  tabletFlex?: string;
}>;

export const SubHeaderTappableListItem = styled.li<
  SubHeaderTappableListItemProps & { marginRight?: string }
>`
  ${({ mobileFlex }) => `
    flex: ${mobileFlex};
  `};

  ${media.tablet`
    ${({ tabletFlex }: SubHeaderTappableListItemProps) => `
      flex: ${tabletFlex};
    `};
  `}
`;
SubHeaderTappableListItem.defaultProps = {
  mobileFlex: '0 1 auto',
  tabletFlex: '0 1 auto',
};
export const TextMedallia = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  ${withSecondaryTypeface}
`;

export const subHeaderTappableStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50px;
  min-width: 50px;
  overflow: hidden;
  border-radius: 10px;
  border: none;
  background: var(--slidein-menu-btn-bg);
  color: ${colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.24px;

  @media (max-width: 340px) {
    font-size: 9px;
    padding: 0 5px;
  }

  & > ${IconRoot} {
    flex: 0 0 auto;
  }

  &[data-small='true'] {
    width: 50px;
  }
`;

export const SubHeaderInternalLink = styled(Link)`
  ${subHeaderTappableStyles};
  ${createExtractCssFromProps({
    padding: 'padding',
  })}
`;
SubHeaderInternalLink.defaultProps = {
  padding: '0 12px',
};

export const SubHeaderExternalLink = styled(ExternalLink)`
  ${subHeaderTappableStyles};
  ${createExtractCssFromProps({
    padding: 'padding',
  })}
`;

SubHeaderExternalLink.defaultProps = {
  padding: '0 12px',
};

export const MenuContent = styled.div`
  width: 100%;
  padding: 0 16px;

  ${media.tablet`
    padding: 0 70px;
  `}
`;

export const CompactTabList = styled(MenuTabsWrapper)`
  && {
    padding: 0;
    height: 23px;
    align-items: center;
  }

  && > li {
    height: 100%;
    align-items: flex-start;
  }
`;

export const CompactTabListLink = styled(MenuTabLink)`
  && {
    height: 100%;
    line-height: 1.5;
    font-size: 12px;
    letter-spacing: 0.24px;
  }

  &&.active {
    line-height: 1.125;
    font-size: 14px;
    letter-spacing: 0.28px;
  }

  &:hover,
  &.active-tab {
    color: var(--slidein-header-tab-link-hover);
  }

  &.active-tab:after,
  &:hover:after {
    background-color: var(--border-color-tab-link-hover);
  }
`;
export const ContainerFooterLink = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: flex-end;
  margin-left: 8px;
  ${media.tablet`
  margin-top:10px;
`}
`;

export const FistLabelFooter = styled.span`
  font-style: normal;
  font-weight: bold;
  font-size: 10px;
  line-height: 10px;
  ${media.tablet`
    font-weight: normal;
    font-size: 12px;
    line-height: 14px;
`}
`;

export const FooterDiv = styled.div`
  margin-bottom: 20px;
  display: flex;
  width: 100%;
  ${media.tablet`
   flex-flow:column;
   align-items:center;
   max-width:140px;
   text-align:center;
   width:100%;
  `};
`;
export const MedalliaFooter = styled.div`
  padding: 12px 16px 12px 16px;
  height: 49px;
  width: 100%;
  display: flex;
  background-color: var(--slidein-menu-btn-bg);
  flex-direction: row;
  flex-wrap: nowrap;
  align-content: center;
  justify-content: flex-start;
  align-items: center;
`;
export const FooterLink = styled(ExternalLink)`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: ${colors.transparent};
  color: inherit;
  overflow: hidden;
  ${withOneLineEllipsis};
  font-size: 14px;
  letter-spacing: 0.28px;
  ${media.tablet`
      justify-content: center;
    `}
  & > span {
    font-weight: bold;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0.32px;
    text-decoration-line: underline;
    color: #ffffff;
    ${media.tablet`
      font-size: 12px;
      line-height: 14px;
    `}
  }
`;

export const ContactUsWrapper = styled(Flex)`
  padding: 16px 16px 36px;
  width: 100%;
  margin-bottom: 16px;
  ${media.tablet`
    align-items: center;
  `}
`;
ContactUsWrapper.defaultProps = {
  flexDirection: 'column',
  flexWrap: 'nowrap',
  justify: 'flex-start',
  align: 'stretch',
};
export const TextContainerMobileFooter = styled.div`
  font-weight: 700;
`;
export const ButtonFooterMedallia = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  display: flex;
  justify-content: center;
  padding: 0;
  align-items: center;
  cursor: pointer;
  color: white;
  ${media.tablet`
    & > div:first-child {
      margin-left:90px;
    }
  `};
`;

export const ContactLinkIconWrapper = styled.a`
  height: 34px;
  width: 34px;
  background: ${colors.darkBackground};
  overflow: hidden;
  border-radius: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  ${media.tablet`
     height: 54px;
     width: 54px;
     justify-content: space-evenly;
  `};
`;

export const ContactLinkAnchor = styled(ExternalLink)`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  color: ${colors.white};
  font-size: 10px;
  text-align: center;
  letter-spacing: 0.2px;

  & > span {
    display: inline-block;
    margin: 8px 0 0;
  }
`;

export const ContactLinkStack = styled(Flex)`
  padding-top: 20px;
  @supports (width: max-content) {
    width: max-content;
  }

  & > *:nth-last-child(n + 2) {
    margin-right: 30px;
  }

  ${media.tablet`
    justify-content: center;
    flex-flow: nowrap;

  `};
`;
ContactLinkStack.defaultProps = {
  justify: 'flex-start',
  flexWrap: 'wrap',
  align: 'stretch',
};

const quickLinkCommonStyles = css`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  color: ${colors.white};
  overflow: hidden;
  border-radius: 7px;
  font-size: 12px;
  height: 40px;
  width: 100%;
  padding: 0 8px;
  letter-spacing: 0.24px;

  ${createExtractCssFromProps({
    background: 'background',
  })};

  & > ${IconRoot} {
    flex: 0 0 auto;
  }

  & > span {
    margin: 0 4px;
  }
`;

export const QuickLink = styled.a`
  ${quickLinkCommonStyles};
`;

export const QuickInternalLink: LinkComponent<'a'> = styled(Link)`
  ${quickLinkCommonStyles};
`;

export const OpenBetsIndicator = styled.div<StyledProps<{ counter?: number }>>`
  display: inline-flex;
  width: 24px;
  overflow: hidden;
  height: 24px;
  flex: 0 0 auto;
  border-radius: 16px;
  background: #d8d8d8;
  justify-content: center;
  align-items: center;
  color: #2f3c43;

  &[hidden] {
    display: none;
  }

  ${({ counter }): string => `
    &:after {
      line-height: 1.33;
      font-size: 12px;
      font-weight: bold;
      letter-spacing: 0.24px;
      content: "${typeof counter === 'number' ? counter : ''}";
    }
  `}
`;

export const TabContentGrid = styled.div<StyledProps<WithPaddingProps>>`
  ${media.ie11.all`
    display: flex;
    align-items: flex-start;
    flex-flow: row wrap;

    & > * {
      flex: 0 0 34%;
      margin-bottom: 24px;
    }

    & > *:nth-child(odd) {
      margin-right: 2%;
    }
  `}

  width: 100%;
  overflow: hidden;

  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: 16px;
  column-gap: 16px;
  grid-row-gap: 24px;
  row-gap: 24px;

  ${withPadding};

  ${media.tablet`
    grid-template-columns: repeat(3, 1fr);
  `}
`;
